import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Session } from '@supabase/supabase-js';
import { onUnauthorized } from '../lib/api-client';
import { isAuthConfigured } from '../lib/env';
import { supabase } from '../lib/supabase';

interface AuthContextValue {
  session: Session | null;
  /** True until the initial session lookup finishes. */
  loading: boolean;
  configured: boolean;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  signUpWithPassword: (email: string, password: string, fullName: string) => Promise<void>;
  sendMagicLink: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function assertConfigured(): NonNullable<typeof supabase> {
  if (!supabase) {
    throw new Error(
      'Supabase chưa được cấu hình. Đặt VITE_SUPABASE_URL và VITE_SUPABASE_ANON_KEY trong web/.env.',
    );
  }
  return supabase;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(isAuthConfigured);
  const queryClient = useQueryClient();

  // Tracks which user the cache currently belongs to. `undefined` means no auth
  // event has been seen yet, which is different from "signed out" (`null`).
  const cachedUserId = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (!supabase) return;
    let active = true;

    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (!active) return;
        setSession(data.session);
        setLoading(false);
      })
      .catch(() => {
        // A misconfigured Supabase URL must not leave the app stuck loading.
        if (active) setLoading(false);
      });

    const { data: subscription } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
      setLoading(false);

      // Cart, library and orders are per-user, so the cache is dropped when the
      // identity changes. It must NOT be dropped on every event: INITIAL_SESSION
      // and TOKEN_REFRESHED also land here, and clearing on those restarts every
      // in-flight query, so the UI never settles.
      const nextUserId = next?.user.id ?? null;
      if (cachedUserId.current !== undefined && cachedUserId.current !== nextUserId) {
        queryClient.clear();
      }
      cachedUserId.current = nextUserId;
    });

    return () => {
      active = false;
      subscription.subscription.unsubscribe();
    };
  }, [queryClient]);

  useEffect(
    // A 401 that survived a token refresh means the session is unusable.
    () => onUnauthorized(() => setSession(null)),
    [],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      loading,
      configured: isAuthConfigured,
      async signInWithPassword(email, password) {
        const { error } = await assertConfigured().auth.signInWithPassword({ email, password });
        if (error) throw new Error(error.message);
      },
      async signUpWithPassword(email, password, fullName) {
        const { error } = await assertConfigured().auth.signUp({
          email,
          password,
          options: { data: { full_name: fullName } },
        });
        if (error) throw new Error(error.message);
      },
      async sendMagicLink(email) {
        const { error } = await assertConfigured().auth.signInWithOtp({
          email,
          options: { emailRedirectTo: `${window.location.origin}/thu-vien` },
        });
        if (error) throw new Error(error.message);
      },
      async signOut() {
        await assertConfigured().auth.signOut();
        queryClient.clear();
      },
    }),
    [session, loading, queryClient],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
